import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  ChevronRight,
  LayoutGrid,
  Inbox,
  Folder,
  Pencil,
  Clock,
  Globe,
  FileText,
  ListPlus,
  Trash,
} from '../lib/icons.tsx';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';
import type { ReactNode } from 'react';
import type { CategoryTreeNode } from '../lib/categoryTree.ts';
import type { CategoryColour } from '../../shared/api.ts';
import { swatchVar } from '../../shared/palette.ts';

// Drag payload type for a deck card dropped onto a category row. A private MIME
// is what lets `dragover` decide whether to accept BEFORE the drop — the payload
// itself is unreadable until then, so anything else would light every row up for
// a dragged file or a selected paragraph.
export const DECK_DRAG_MIME = 'application/x-rkh-deck';

/**
 * One row of the optional Views section — the slices of the collection that
 * sit above the folder tree ("All decks", "Drafts", "Trash"…).
 *
 * Deliberately says nothing about decks or filters: the rail is shared with the
 * Library and the Bank, so what a view MEANS stays with its caller and only the
 * three things a row draws (a name, a tally, whether it is the current one)
 * cross the boundary. The Library derives these in
 * `routes/library/libraryViews.ts`; the decks page's rail is folders only.
 */
export type SidebarViewRow = {
  /** Stable, and the key the built-in icon map reads. */
  id: string;
  label: string;
  count?: number;
  active?: boolean;
  /** Overrides the icon the id would pick — for a view this rail has never
   *  heard of. */
  icon?: ReactNode;
};

/**
 * The glyph each known view wears. Kept here rather than passed in so wiring a
 * Views section costs the caller one array of plain data, and so two pages
 * asking for "Drafts" can't end up with different icons. An id with no entry
 * falls back to the folder glyph, which is wrong-ish but never blank.
 */
const VIEW_ICONS: Record<string, ReactNode> = {
  all: <LayoutGrid />,
  recent: <Clock />,
  published: <Globe />,
  drafts: <FileText />,
  trash: <Trash />,
  // The Library's two (Run 10). Both are about an item LEAVING the shelf for a
  // deck, which is why neither wears the folder glyph.
  inserted: <Clock />,
  used: <ListPlus />,
};

/**
 * Whether a rail section is open, remembered per SECTION rather than per page.
 *
 * The rail draws at most two of them — Views (the Library) and Folders (both
 * pages) — and the answer is a lasting preference about how much navigation you
 * want on screen, not something to re-decide on every visit. Keyed by section
 * rather than by surface because the question is the same one on both: a person
 * who folds their folders away on the decks page means it on the Library too.
 *
 * Storage can throw outright (a private window, site data blocked), so both
 * halves are guarded and an unreadable store simply means "open", which is the
 * state the rail has always had.
 */
const SECTION_OPEN_KEY = (id: string) => `studio.rail.section.${id}`;

function readSectionOpen(id: string): boolean {
  try {
    return localStorage.getItem(SECTION_OPEN_KEY(id)) !== 'closed';
  } catch {
    return true;
  }
}

function writeSectionOpen(id: string, open: boolean): void {
  try {
    localStorage.setItem(SECTION_OPEN_KEY(id), open ? 'open' : 'closed');
  } catch {
    /* storage unavailable — the section still collapses for this visit */
  }
}

/**
 * A section heading that folds its rows away.
 *
 * The heading stays a real `<h2>` — it is what a screen reader lands on between
 * the sections — with the control inside it rather than instead of it, so the
 * document outline is unchanged by making it collapsible.
 *
 * `chip` is the half that is easy to leave out: collapsed, the rows that said
 * which folder is on are not on screen, so the heading has to say it instead or
 * the rail becomes a filter you cannot see. Same rule as the phone summary.
 */
function SectionHeading({
  id,
  label,
  open,
  onToggle,
  chip,
}: {
  id: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  chip?: { label: string; count?: number } | null;
}) {
  return (
    <h2 className="cat-sidebar__caption">
      <button
        type="button"
        className="cat-sidebar__caption-btn"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className={'cat-row__chevron' + (open ? ' is-open' : '')} aria-hidden="true">
          <ChevronRight />
        </span>
        <span className="cat-sidebar__caption-text">{label}</span>
      </button>
      {!open && chip && (
        <span className="cat-sidebar__caption-chip">
          <span className="cat-sidebar__caption-chip-label">{chip.label}</span>
          {chip.count != null && <span className="cat-row__count">{chip.count}</span>}
        </span>
      )}
    </h2>
  );
}

/**
 * A category's registry tint, drawn as the design's dot.
 *
 * The colour is a TOKEN NAME (`orange`, `lime`…) turned into a custom property,
 * never a literal — the hues live once in base.css, which is what keeps both
 * themes and any later palette correction in one place. The same rule the
 * filter chips follow.
 *
 * An untinted category is the DEFAULT, not a gap: it keeps the folder glyph,
 * and only a child row inside a group where something is tinted draws the
 * (empty) dot, purely so its siblings' labels stay on one left edge.
 */
function CategoryDot({ colour }: { colour?: CategoryColour | null }) {
  return (
    <span
      className={'cat-row__dot' + (colour ? '' : ' cat-row__dot--none')}
      style={colour ? { background: swatchVar(colour) } : undefined}
      aria-hidden="true"
    />
  );
}

/**
 * The dashboard's category navigation — a left-rail tree of the deck
 * collection's organisation categories (a deck's one "home"). Two levels:
 * parents expand to sub-categories. Selecting "All decks", a parent (shows it +
 * all its children), a child, or "Uncategorised" filters the grid.
 *
 * Presentational only: the Dashboard derives `tree` (parents with children +
 * counts) and owns the active selection; this component just renders and
 * reports clicks. `active` is null (All), the `uncategorisedKey` sentinel, or a
 * category path. `onManage`, when provided, renders the "Manage categories"
 * button (Phase 3).
 *
 * `onDropDeck(path, id)`, when provided, makes every category row (and
 * Uncategorised) a drop target for a dragged deck card — the fast path for
 * filing, next to the card's own Categories… dialog. Optional, so the Library
 * and Bank rails are unchanged.
 */
type CategorySidebarProps = {
  tree: CategoryTreeNode[];
  /** The selected path, or the all/uncategorised sentinel. */
  active?: string | null;
  allCount?: number;
  uncategorisedCount?: number;
  // (uncategorisedKey below is a sentinel path, never absent in practice)
  uncategorisedKey?: string | null;
  onSelect: (path: string | null) => void;
  onManage?: () => void;
  /** Filing by drag: the rail is a drop target while a card is in flight. */
  onDropDeck?: (path: string | null, deckId: string) => void;
  dragging?: boolean;
  /** Overridable so the same rail serves the decks dashboard, the Library and
   *  the Bank; the defaults are the deck wording. */
  ariaLabel?: string;
  allLabel?: string;
  allIcon?: ReactNode;
  manageLabel?: string;
  manageIcon?: ReactNode;
  /** The optional Views section above the folders — the LIBRARY's rows today
   *  (All saved slides / Recently inserted / Most used); the decks page and the
   *  Bank pass nothing. Providing it also takes the "All decks" row OFF the top
   *  of the tree: the Views section owns that row, and two of them would be one
   *  too many.
   *
   *  The decks page had five of these (All decks / Recently opened / Published
   *  / Drafts / Trash) and dropped them on 2026-09-07: every one of them was
   *  also a Status pill option, a Sort option, the Jump back in strip or a
   *  chip, so the rail was a second control for questions the bar already
   *  asked. The prop stays because the Library's rows are the shelf's ONLY
   *  control for their axes. */
  views?: SidebarViewRow[];
  onSelectView?: (id: string) => void;
  /** The caption over the folder tree. Always rendered — it is the heading the
   *  collapse toggle lives on — so this is what it reads. */
  foldersLabel?: string;
  /** Anything the page wants at the FOOT of the rail, under Manage categories:
   *  the Library's "How saving works" panel is the only caller. Deliberately a
   *  slot rather than a named panel — the rail is shared with two other pages
   *  and must not learn what saving is. It renders INSIDE the collapsible tree,
   *  so a phone folds it away with the rest of the navigation. */
  footer?: ReactNode;
};

export default function CategorySidebar({
  tree,
  active,
  allCount,
  uncategorisedCount,
  uncategorisedKey,
  onSelect,
  onManage,
  onDropDeck,
  dragging = false,
  // Overridable so the same rail serves the decks dashboard, the Library and
  // the Bank; the defaults are the deck wording.
  ariaLabel = 'Deck categories',
  allLabel = 'All decks',
  allIcon = <LayoutGrid />,
  manageLabel = 'Manage categories',
  manageIcon = <Pencil />,
  views,
  onSelectView,
  foldersLabel = 'Folders',
  footer,
}: CategorySidebarProps) {
  // A Views section only exists if a caller both supplies rows and can act on a
  // click; half of the pair would render rows that do nothing.
  const hasViews = !!views?.length && !!onSelectView;
  // Parents start collapsed; the one containing the active selection opens.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Whole-SECTION collapse, remembered. A different question from the
  // per-parent `expanded` above and from the phone rail's `railOpen` below:
  // this one is a desktop preference about how much of the rail you want, and
  // it is the only one of the three that persists.
  const [viewsOpen, setViewsOpen] = useState(() => readSectionOpen('views'));
  const [foldersOpen, setFoldersOpen] = useState(() => readSectionOpen('folders'));
  const toggleSection = (id: 'views' | 'folders') => {
    const setter = id === 'views' ? setViewsOpen : setFoldersOpen;
    setter((open) => {
      writeSectionOpen(id, !open);
      return !open;
    });
  };

  // The WHOLE rail collapses on a phone, which is a different question from
  // the per-parent `expanded` above. Stacked above the grid it was a wrapping
  // block of every category — on the reported screen it filled most of the
  // viewport before a single deck card, which is what this exists to stop.
  //
  // Deliberately NOT persisted: the ask is a DEFAULT, re-opening costs one
  // tap, and a remembered answer would sooner or later contradict the default
  // it is meant to express. It is also only ever READ on a phone, so a tap
  // here can never change how the desktop rail looks.
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [railOpen, setRailOpen] = useState(false);
  const isOpen = (path: string): boolean =>
    expanded[path] ?? (typeof active === 'string' && active !== uncategorisedKey && active.startsWith(path + '/'));
  const toggle = (path: string) => setExpanded((prev) => ({ ...prev, [path]: !isOpen(path) }));

  // ---- deck drag-and-drop -------------------------------------------------
  // The row currently under the pointer, so it can say "drop here". `dragover`
  // fires continuously and `dragleave` fires when crossing to a CHILD element,
  // so the target is keyed by path and only cleared when another row claims it
  // (or the drop/leave-the-rail ends the gesture) — clearing on every dragleave
  // makes the highlight strobe as the pointer crosses the row's own label.
  const [dropPath, setDropPath] = useState<string | null>(null);
  // Hovering a collapsed parent for a beat opens it, so its children can be
  // reached without dropping first. Spring-loading, as Finder does it.
  const hoverOpen = useRef<{ path: string | null; timer: ReturnType<typeof setTimeout> | null }>({
    path: null,
    timer: null,
  });
  useEffect(() => () => clearTimeout(hoverOpen.current.timer ?? undefined), []);

  const carriesDeck = (e: DragEvent<HTMLElement>) =>
    !!onDropDeck && Array.from(e.dataTransfer.types || []).includes(DECK_DRAG_MIME);

  function springLoad(path: string, hasChildren: boolean) {
    if (hoverOpen.current.path === path) return;
    clearTimeout(hoverOpen.current.timer ?? undefined);
    hoverOpen.current = { path, timer: null };
    if (!hasChildren || isOpen(path)) return;
    hoverOpen.current.timer = setTimeout(() => setExpanded((prev) => ({ ...prev, [path]: true })), 600);
  }

  // Wire a row up as a drop target. Returns nothing when dropping is disabled,
  // so the rows spread `{...dropProps(...)}` unconditionally.
  function dropProps(path: string | null, { hasChildren = false }: { hasChildren?: boolean } = {}) {
    if (!onDropDeck) return {};
    return {
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (!carriesDeck(e)) return;
        e.preventDefault(); // the only way to say "this is a valid drop"
        e.dataTransfer.dropEffect = 'copy';
        setDropPath(path);
        if (path) springLoad(path, hasChildren);
      },
      onDragLeave: (e: DragEvent<HTMLElement>) => {
        // Only when the pointer really left this row, not on a hop between its
        // own chevron and label. Chromium leaves `relatedTarget` null on drag
        // events, so this has to be answered from the POINTER's position: with
        // the usual relatedTarget test the row un-highlighted (and, worse,
        // cancelled its spring-load timer) every time the pointer crossed from
        // the row onto its own label.
        const r = e.currentTarget.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (inside) return;
        setDropPath((cur) => (cur === path ? null : cur));
        if (hoverOpen.current.path === path) {
          clearTimeout(hoverOpen.current.timer ?? undefined);
          hoverOpen.current = { path: null, timer: null };
        }
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (!carriesDeck(e)) return;
        e.preventDefault();
        setDropPath(null);
        clearTimeout(hoverOpen.current.timer ?? undefined);
        hoverOpen.current = { path: null, timer: null };
        const id = e.dataTransfer.getData(DECK_DRAG_MIME);
        if (id) onDropDeck(path, id);
      },
    };
  }

  const dropClass = (path: string | null) => (dropPath === path ? ' is-drop-target' : '');

  // What the collapsed control SAYS. A chevron alone would make the closed
  // rail a question mark — the house rule is that a control states its current
  // answer, so the summary carries the selected category and its count and the
  // filter stays legible without opening anything.
  function currentSelection(): { label: string; count?: number } {
    if (active === null || active === undefined) {
      // With a Views section, "All decks" is only the truth while All decks is
      // the lit row: on a phone, Trash or Drafts collapsed to a control saying
      // "All decks" is a filter you cannot see and would not guess.
      const view = views?.find((v) => v.active);
      if (view) return { label: view.label, count: view.count };
      return { label: allLabel as string, count: allCount };
    }
    if (uncategorisedKey && active === uncategorisedKey) return { label: 'Uncategorised', count: uncategorisedCount };
    for (const node of tree) {
      if (node.path === active) return { label: node.label, count: node.count };
      for (const child of node.children) {
        // Full path, not the leaf: "Pitches" alone doesn't say which parent,
        // and two parents may each have one.
        if (child.path === active) return { label: `${node.label} / ${child.label}`, count: child.count };
      }
    }
    // A category that has since been deleted or renamed — say the raw path
    // rather than silently claiming "All decks".
    return { label: String(active).split('/').join(' / ') };
  }
  const selection = currentSelection();
  const railHidden = isPhone && !railOpen;
  const activeView = views?.find((v) => v.active) ?? null;
  // Only when a folder really is chosen: "All decks" collapsed away says
  // nothing, and a chip repeating it would read as a filter.
  const folderChip = active === null || active === undefined ? null : selection;

  return (
    <nav className={'cat-sidebar' + (isPhone ? ' cat-sidebar--phone' : '')} aria-label={ariaLabel}>
      {isPhone && (
        <button
          type="button"
          className="cat-sidebar__summary"
          aria-expanded={railOpen}
          aria-controls="cat-sidebar-tree"
          onClick={() => setRailOpen((v) => !v)}
        >
          <span className="cat-row__icon">{allIcon}</span>
          <span className="cat-sidebar__summary-label">{selection.label}</span>
          {selection.count != null && <span className="cat-row__count">{selection.count}</span>}
          <span className={'cat-row__chevron' + (railOpen ? ' is-open' : '')} aria-hidden="true">
            <ChevronRight />
          </span>
        </button>
      )}
      {/* `hidden` rather than unmounting: the tree holds per-parent expand
          state and the drag-and-drop targets, and throwing that away on every
          collapse would reset where the user had got to. */}
      <div id="cat-sidebar-tree" className="cat-sidebar__tree" hidden={railHidden}>
        {/* Views: the slices of the whole collection, above the folders. Inside
          the collapsible tree rather than beside it, so the phone rail folds
          the WHOLE navigation away and not just its lower half. */}
        {hasViews && (
          <div className="cat-sidebar__group" data-section="views">
            <SectionHeading
              id="cat-sidebar-views"
              label="Views"
              open={viewsOpen}
              onToggle={() => toggleSection('views')}
              chip={activeView ? { label: activeView.label, count: activeView.count } : null}
            />
            <div
              id="cat-sidebar-views"
              className="cat-sidebar__section"
              role="group"
              aria-label="Views"
              hidden={!viewsOpen}
            >
              {views?.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  className={'cat-row cat-row--top' + (view.active ? ' is-active' : '')}
                  aria-current={view.active ? 'true' : undefined}
                  // The row's id, on the row. Nothing in the app reads it: it is
                  // there so the headless UI check can name a view without matching
                  // on its label, which is copy and will change.
                  data-view-id={view.id}
                  onClick={() => onSelectView?.(view.id)}
                >
                  <span className="cat-row__icon">{view.icon ?? VIEW_ICONS[view.id] ?? <Folder />}</span>
                  <span className="cat-row__label">{view.label}</span>
                  {view.count != null && <span className="cat-row__count">{view.count}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Folders. The heading is always drawn now (2026-09-07): it carries the
          collapse toggle, and a section that folds has to have somewhere to say
          so. Collapsed, its chip names the folder in force — the same rule the
          phone summary follows, because a filter nobody can see is worse than
          no filter at all. */}
        <div className="cat-sidebar__group" data-section="folders">
          <SectionHeading
            id="cat-sidebar-folders"
            label={foldersLabel}
            open={foldersOpen}
            onToggle={() => toggleSection('folders')}
            chip={folderChip}
          />
          <div id="cat-sidebar-folders" className="cat-sidebar__section" hidden={!foldersOpen}>
            {!hasViews && (
              <button
                type="button"
                className={'cat-row cat-row--top' + (active === null ? ' is-active' : '')}
                aria-current={active === null ? 'true' : undefined}
                onClick={() => onSelect(null)}
              >
                <span className="cat-row__icon">{allIcon}</span>
                <span className="cat-row__label">{allLabel}</span>
                <span className="cat-row__count">{allCount}</span>
              </button>
            )}

            {tree.map((node) => {
              const hasChildren = node.children.length > 0;
              const open = hasChildren && isOpen(node.path);
              const parentActive = active === node.path;
              // Children have no icon column of their own, so a tinted one would sit
              // 9px right of its untinted siblings. The reservation is per GROUP: a
              // rail with no colours anywhere (the Library, the Bank) is untouched.
              const tintedChildren = node.children.some((c) => c.colour);
              return (
                <div key={node.path} className="cat-node">
                  <div
                    className={'cat-row' + (parentActive ? ' is-active' : '') + dropClass(node.path)}
                    {...dropProps(node.path, { hasChildren })}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        className={'cat-row__chevron' + (open ? ' is-open' : '')}
                        aria-label={open ? `Collapse ${node.label}` : `Expand ${node.label}`}
                        aria-expanded={open}
                        onClick={() => toggle(node.path)}
                      >
                        <ChevronRight />
                      </button>
                    ) : (
                      <span className="cat-row__chevron cat-row__chevron--leaf" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      className="cat-row__main"
                      aria-current={parentActive ? 'true' : undefined}
                      onClick={() => onSelect(node.path)}
                    >
                      <span className="cat-row__icon">
                        {node.colour ? <CategoryDot colour={node.colour} /> : <Folder />}
                      </span>
                      <span className="cat-row__label">{node.label}</span>
                      <span className="cat-row__count">{node.count}</span>
                    </button>
                  </div>
                  {open && (
                    <div className="cat-children">
                      {node.children.map((child) => (
                        <button
                          key={child.path}
                          type="button"
                          className={
                            'cat-row cat-row--child' +
                            (active === child.path ? ' is-active' : '') +
                            dropClass(child.path)
                          }
                          aria-current={active === child.path ? 'true' : undefined}
                          onClick={() => onSelect(child.path)}
                          {...dropProps(child.path)}
                        >
                          {tintedChildren && <CategoryDot colour={child.colour} />}
                          <span className="cat-row__label">{child.label}</span>
                          <span className="cat-row__count">{child.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Also shown WHILE a deck is being dragged even when empty: it is the
          drop target that means "take this out of its categories", and a row
          that isn't on screen can't be dropped on. */}
            {((uncategorisedCount ?? 0) > 0 || (onDropDeck && dragging)) && (
              <button
                type="button"
                className={
                  'cat-row cat-row--top' +
                  (active === uncategorisedKey ? ' is-active' : '') +
                  dropClass(uncategorisedKey ?? null)
                }
                aria-current={active === uncategorisedKey ? 'true' : undefined}
                onClick={() => onSelect(uncategorisedKey ?? null)}
                {...dropProps(uncategorisedKey ?? null)}
              >
                <span className="cat-row__icon">
                  <Inbox />
                </span>
                <span className="cat-row__label">Uncategorised</span>
                <span className="cat-row__count">{uncategorisedCount}</span>
              </button>
            )}

            {onDropDeck && dragging && <p className="cat-sidebar__drophint">Drop on a category to file this deck.</p>}
          </div>
        </div>

        {onManage && (
          <button type="button" className="cat-sidebar__manage" onClick={onManage}>
            {manageIcon} <span>{manageLabel}</span>
          </button>
        )}

        {footer}

        {/* A PEOPLE section belongs here, under the folders, the same shape as
            Views: rows of name + count, tinted with the person's colour. The
            identity it was waiting on arrived with Google sign-in; what is
            still missing is the FILTER, since this would filter decks by owner
            rather than manage the list (that is ⚙ → People). The design's TAGS
            section is not coming: folders are the single axis, and their colour
            is the dot. */}
      </div>
    </nav>
  );
}
